import { Component, OnChanges, Input, ViewChild, ElementRef, SimpleChanges, HostListener, Output, EventEmitter, OnInit } from '@angular/core';

import { DistinctPipe } from './pipe/distinct.pipe';
import { FormatPipe } from './pipe/format.pipe';
import { SortPipe } from './pipe/sort.pipe';

import { Column } from './models/column';
import { EventData } from './models/eventdata';
import { ValueField } from './models/valuefield';
import { MathFunction } from './models/mathfunction';
import { Distinct } from './models/distinct';
import { PivotSave, ToolbarConfig } from './models/pivotsave';

import { MDGPivotDrawerComponent } from './component/pivot/mdg.pivotdrawer';
import { MDGToolBarComponent } from './component/toolbar/mdg.toolbar';

import { HelperService } from './Services/Helper.Service';

const ISO_8601_FULL = /^\d{4}-\d\d-\d\dT\d\d:\d\d:\d\d(\.\d+)?(([+-]\d\d:\d\d)|Z)?$/i;

@Component({
  selector: 'mdg-pivot',
  template: `<div class='mdg-pivot-container' [class.full-screen]='isFullScreen' #container>
  <mdg-toolbar #toolbar [dataLoading]='isLoading' [isCustomSave]='isCustomSave' [isSaveSubscribed]='isSaveSubscribed' [isFullScreen]='isFullScreen' [toolbarConfig]='toolbarConfig' [formulas]='formulas' (applyChanges)='onApplyChanges($event)'
    (exportReport)='onExportReport($event)' (showFullScreen)='onShowFullScreen($event)' (updateFormat)='onUpdateFormat($event)'
    (saveReport)='onSaveReport($event)' (changeChartType)='onChangeChartType($event)' [pivotConfig]='pivotConfig'
    (error)='onError($event)'></mdg-toolbar>
  <mdg-pivotdrawer #pivot [formulas]='formulas' [columns]='columns' [chartType]='pivotConfig.reportType' [title]='pivotConfig.reportTitle'
    [selectedColumns]='selectedColumns' [selectedRows]='selectedRows' [selectedValues]='selectedValues' [pivotConfig]='pivotConfig'
    [selectedFColumns]='selectedFColumns' [noRecordsMessage]='noRecordsMessage' (loading)='onLoading($event)' (error)='onError($event)' [filters]='filters'></mdg-pivotdrawer>
</div> `,
  styles: [
  ]
})
export class MdgPivotComponent implements OnInit, OnChanges {
  @Input() data: any[] = [];
  @Input() autoConfigurePivot: boolean = true;
  @Input() pivotFormat: any = undefined;
  @Input() toolbarFormat: ToolbarConfig = undefined;
  @Input() isLoading: boolean = false;
  @Input() isCustomSave: boolean = false;
  @Input() noRecordsMessage: string = 'No data to display.';

  @ViewChild('pivot') pivot: MDGPivotDrawerComponent;
  @ViewChild('toolbar') toolbar: MDGToolBarComponent;
  @ViewChild('container') container: ElementRef;

  @Output() savePivot: EventEmitter<any> = new EventEmitter<any>();
  @Output() error: EventEmitter<any> = new EventEmitter<any>();

  pivotConfig: PivotSave;
  toolbarConfig: ToolbarConfig = new ToolbarConfig();
  columns: Column[] = [];
  distValues: Distinct[] = [];
  formattedFields: any = {};
  selectedColumns: Column[] = [];
  selectedFColumns: Column[] = [];
  selectedRows: Column[] = [];
  formulaFields: Column[] = [];
  selectedValues: ValueField[] = [];
  formulas: MathFunction[] = [];
  filters: any[] = [];
  isFullScreen: boolean = false;

  isSaveSubscribed: boolean = false;
  isErrorSubscribed: boolean = false;

  constructor(private helper: HelperService, private distinctPipe: DistinctPipe, private sort: SortPipe, private format: FormatPipe) {
    this.formulas.push({ Name: 'Sum', Symbol: '&Sigma;', funcName: 'sum' });
    this.formulas.push({ Name: 'Count', Symbol: '', funcName: 'count' });
    this.formulas.push({ Name: 'Distinct Count', Symbol: '', funcName: 'dcount' });
    this.formulas.push({ Name: 'Average', Symbol: '', funcName: 'avg' });
    this.formulas.push({ Name: 'Min', Symbol: '', funcName: 'min' });
    this.formulas.push({ Name: 'Max', Symbol: '', funcName: 'max' });
    // this.formulas.push({ Name: 'Percentage', Symbol: '', funcName: 'prcnt' });
    this.formulas.push({ Name: 'Product', Symbol: '', funcName: 'product' });
    this.formulas.push({ Name: 'Range', Symbol: '', funcName: 'range' });
    this.formulas.push({ Name: 'Mid-Range', Symbol: '', funcName: 'midrange' });
    this.formulas.push({ Name: 'Median', Symbol: '', funcName: 'median' });
    this.formulas.push({ Name: 'Variance', Symbol: '', funcName: 'var' });
    this.formulas.push({ Name: 'Standard Deviation', Symbol: '', funcName: 'stddvn' });
    this.formulas.push({ Name: 'Adsolute Deviation', Symbol: '', funcName: 'absdvn' });
  }

  ngOnInit(): void {
    this.isSaveSubscribed = this.savePivot.observers.length > 0;
    this.isErrorSubscribed = this.error.observers.length > 0;
    this.pivotConfig = new PivotSave();
    this.pivotConfig.formatName = 'Pivot';
    this.pivotConfig.reportTitle = 'Pivot Report';
    this.pivotConfig.reportType = 'grid';
    this.pivotConfig.showExport = true;
    this.pivotConfig.showFilter = true;
    this.pivotConfig.showAdditionalFilter = true;
    this.pivotConfig.showColumnFilter = true;
    this.pivotConfig.showFormat = true;
    this.pivotConfig.showOptions = true;
    this.pivotConfig.showPrint = true;
    this.pivotConfig.showSave = true;
    this.pivotConfig.lineChart = true;
    this.pivotConfig.barChart = true;
    this.pivotConfig.doughnutChart = true;
    this.pivotConfig.pieChart = true;
    this.pivotConfig.polarAreaChart = true;
    this.pivotConfig.radarChart = true;
    this.copytoolbarFormat();
  }

  @HostListener('document: fullscreenchange', ['$event'])
  onFullScreenChange(ev: Event) {
    this.checkFullScreen();
  }

  @HostListener('document: webkitfullscreenchange', ['$event'])
  onWebkitFullScreenChange(ev: Event) {
    this.checkFullScreen();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.data !== undefined && changes.data.currentValue && changes.data.currentValue.length > 0 && changes.data.currentValue !== changes.data.previousValue) {
      this.findColumns();
      this.setDefaults();
    }
    if (changes.toolbarFormat !== undefined && changes.toolbarFormat.currentValue !== changes.toolbarFormat.previousValue) {
      this.copytoolbarFormat();
    }
  }

  onChangeChartType(ct: any) {
    if (this.pivot && this.pivot !== null) {
      this.pivot.updateChartType(ct);
    }
  }

  refreshData(data: any) {
    this.data = data;
    this.findColumns();
    this.setDefaults();
  }

  checkFullScreen() {
    const document: any = window.document;
    if (document.webkitIsFullScreen) {
      this.isFullScreen = true;
    } else {
      this.isFullScreen = false;
    }
  }

  copytoolbarFormat() {
    if (this.toolbarFormat && this.toolbarFormat !== null) {
      const toolbar = new ToolbarConfig();
      if (this.toolbarFormat.class && this.toolbarFormat.class !== null && this.toolbarFormat.class.trim()) {
        toolbar.class = this.toolbarFormat.class;
      }
      if (this.toolbarFormat.optionsConfig && this.toolbarFormat.optionsConfig != null) {
        if (this.toolbarFormat.optionsConfig.class && this.toolbarFormat.optionsConfig.class !== null && this.toolbarFormat.optionsConfig.class.trim()) {
          toolbar.optionsConfig.class = this.toolbarFormat.optionsConfig.class;
        }
        if (this.toolbarFormat.optionsConfig.icon && this.toolbarFormat.optionsConfig.icon !== null && this.toolbarFormat.optionsConfig.icon.trim()) {
          toolbar.optionsConfig.icon = this.toolbarFormat.optionsConfig.icon;
        }
        if (this.toolbarFormat.optionsConfig.text && this.toolbarFormat.optionsConfig.text !== null && this.toolbarFormat.optionsConfig.text.trim()) {
          toolbar.optionsConfig.text = this.toolbarFormat.optionsConfig.text;
        }
        if (this.toolbarFormat.optionsConfig.title && this.toolbarFormat.optionsConfig.title !== null && this.toolbarFormat.optionsConfig.title.trim()) {
          toolbar.optionsConfig.title = this.toolbarFormat.optionsConfig.title;
        }
      }
      if (this.toolbarFormat.filterConfig && this.toolbarFormat.filterConfig != null) {
        if (this.toolbarFormat.filterConfig.class && this.toolbarFormat.filterConfig.class !== null && this.toolbarFormat.filterConfig.class.trim()) {
          toolbar.filterConfig.class = this.toolbarFormat.filterConfig.class;
        }
        if (this.toolbarFormat.filterConfig.icon && this.toolbarFormat.filterConfig.icon !== null && this.toolbarFormat.filterConfig.icon.trim()) {
          toolbar.filterConfig.icon = this.toolbarFormat.filterConfig.icon;
        }
        if (this.toolbarFormat.filterConfig.text && this.toolbarFormat.filterConfig.text !== null && this.toolbarFormat.filterConfig.text.trim()) {
          toolbar.filterConfig.text = this.toolbarFormat.filterConfig.text;
        }
        if (this.toolbarFormat.filterConfig.title && this.toolbarFormat.filterConfig.title !== null && this.toolbarFormat.filterConfig.title.trim()) {
          toolbar.filterConfig.title = this.toolbarFormat.filterConfig.title;
        }
      }
      if (this.toolbarFormat.formatConfig && this.toolbarFormat.formatConfig != null) {
        if (this.toolbarFormat.formatConfig.class && this.toolbarFormat.formatConfig.class !== null && this.toolbarFormat.formatConfig.class.trim()) {
          toolbar.formatConfig.class = this.toolbarFormat.formatConfig.class;
        }
        if (this.toolbarFormat.formatConfig.icon && this.toolbarFormat.formatConfig.icon !== null && this.toolbarFormat.formatConfig.icon.trim()) {
          toolbar.formatConfig.icon = this.toolbarFormat.formatConfig.icon;
        }
        if (this.toolbarFormat.formatConfig.text && this.toolbarFormat.formatConfig.text !== null && this.toolbarFormat.formatConfig.text.trim()) {
          toolbar.formatConfig.text = this.toolbarFormat.formatConfig.text;
        }
        if (this.toolbarFormat.formatConfig.title && this.toolbarFormat.formatConfig.title !== null && this.toolbarFormat.formatConfig.title.trim()) {
          toolbar.formatConfig.title = this.toolbarFormat.formatConfig.title;
        }
      }
      if (this.toolbarFormat.pivotTypeConfig && this.toolbarFormat.pivotTypeConfig != null) {
        if (this.toolbarFormat.pivotTypeConfig.class && this.toolbarFormat.pivotTypeConfig.class !== null && this.toolbarFormat.pivotTypeConfig.class.trim()) {
          toolbar.pivotTypeConfig.class = this.toolbarFormat.pivotTypeConfig.class;
        }
        if (this.toolbarFormat.pivotTypeConfig.title && this.toolbarFormat.pivotTypeConfig.title !== null && this.toolbarFormat.pivotTypeConfig.title.trim()) {
          toolbar.pivotTypeConfig.title = this.toolbarFormat.pivotTypeConfig.title;
        }
      }
      if (this.toolbarFormat.exportConfig && this.toolbarFormat.exportConfig != null) {
        if (this.toolbarFormat.exportConfig.class && this.toolbarFormat.exportConfig.class !== null && this.toolbarFormat.exportConfig.class.trim()) {
          toolbar.exportConfig.class = this.toolbarFormat.exportConfig.class;
        }
        if (this.toolbarFormat.exportConfig.icon && this.toolbarFormat.exportConfig.icon !== null && this.toolbarFormat.exportConfig.icon.trim()) {
          toolbar.exportConfig.icon = this.toolbarFormat.exportConfig.icon;
        }
        if (this.toolbarFormat.exportConfig.text && this.toolbarFormat.exportConfig.text !== null && this.toolbarFormat.exportConfig.text.trim()) {
          toolbar.exportConfig.text = this.toolbarFormat.exportConfig.text;
        }
        if (this.toolbarFormat.exportConfig.title && this.toolbarFormat.exportConfig.title !== null && this.toolbarFormat.exportConfig.title.trim()) {
          toolbar.exportConfig.title = this.toolbarFormat.exportConfig.title;
        }
      }
      if (this.toolbarFormat.saveConfig && this.toolbarFormat.saveConfig != null) {
        if (this.toolbarFormat.saveConfig.class && this.toolbarFormat.saveConfig.class !== null && this.toolbarFormat.saveConfig.class.trim()) {
          toolbar.saveConfig.class = this.toolbarFormat.saveConfig.class;
        }
        if (this.toolbarFormat.saveConfig.icon && this.toolbarFormat.saveConfig.icon !== null && this.toolbarFormat.saveConfig.icon.trim()) {
          toolbar.saveConfig.icon = this.toolbarFormat.saveConfig.icon;
        }
        if (this.toolbarFormat.saveConfig.text && this.toolbarFormat.saveConfig.text !== null && this.toolbarFormat.saveConfig.text.trim()) {
          toolbar.saveConfig.text = this.toolbarFormat.saveConfig.text;
        }
        if (this.toolbarFormat.saveConfig.title && this.toolbarFormat.saveConfig.title !== null && this.toolbarFormat.saveConfig.title.trim()) {
          toolbar.saveConfig.title = this.toolbarFormat.saveConfig.title;
        }
      }
      if (this.toolbarFormat.fullscreenConfig && this.toolbarFormat.fullscreenConfig != null) {
        if (this.toolbarFormat.fullscreenConfig.class && this.toolbarFormat.fullscreenConfig.class !== null && this.toolbarFormat.fullscreenConfig.class.trim()) {
          toolbar.fullscreenConfig.class = this.toolbarFormat.fullscreenConfig.class;
        }
        if (this.toolbarFormat.fullscreenConfig.icon && this.toolbarFormat.fullscreenConfig.icon !== null && this.toolbarFormat.fullscreenConfig.icon.trim()) {
          toolbar.fullscreenConfig.icon = this.toolbarFormat.fullscreenConfig.icon;
        }
        if (this.toolbarFormat.fullscreenConfig.closeIcon && this.toolbarFormat.fullscreenConfig.closeIcon !== null && this.toolbarFormat.fullscreenConfig.closeIcon.trim()) {
          toolbar.fullscreenConfig.closeIcon = this.toolbarFormat.fullscreenConfig.closeIcon;
        }
        if (this.toolbarFormat.fullscreenConfig.text && this.toolbarFormat.fullscreenConfig.text !== null && this.toolbarFormat.fullscreenConfig.text.trim()) {
          toolbar.fullscreenConfig.text = this.toolbarFormat.fullscreenConfig.text;
        }
        if (this.toolbarFormat.fullscreenConfig.closeText && this.toolbarFormat.fullscreenConfig.closeText !== null && this.toolbarFormat.fullscreenConfig.closeText.trim()) {
          toolbar.fullscreenConfig.closeText = this.toolbarFormat.fullscreenConfig.closeText;
        }
        if (this.toolbarFormat.fullscreenConfig.title && this.toolbarFormat.fullscreenConfig.title !== null && this.toolbarFormat.fullscreenConfig.title.trim()) {
          toolbar.fullscreenConfig.title = this.toolbarFormat.fullscreenConfig.title;
        }
        if (this.toolbarFormat.fullscreenConfig.closeTitle && this.toolbarFormat.fullscreenConfig.closeTitle !== null && this.toolbarFormat.fullscreenConfig.closeTitle.trim()) {
          toolbar.fullscreenConfig.closeTitle = this.toolbarFormat.fullscreenConfig.closeTitle;
        }
      }
      this.toolbarConfig = toolbar;
    }
  }

  setDefaults() {
    this.selectedColumns = [];
    this.selectedRows = [];
    this.selectedValues = [];
    this.formattedFields = {};
    if (this.pivotFormat !== undefined) {
      this.pivotConfig.formatName = this.pivotFormat['name'];
      this.pivotConfig.reportTitle = this.pivotFormat['title'];
      this.pivotConfig.reportType = this.pivotFormat['reportType'];
      this.pivotConfig.showExport = this.pivotFormat['showExport'];
      this.pivotConfig.showFilter = this.pivotFormat['showFilter'];
      this.pivotConfig.showAdditionalFilter = this.pivotFormat['showAdditionalFilter'] !== undefined && this.pivotFormat['showAdditionalFilter'] !== null ? this.pivotFormat['showAdditionalFilter'] : true;
      this.pivotConfig.showColumnFilter = this.pivotFormat['showColumnFilter'] !== undefined && this.pivotFormat['showColumnFilter'] !== null ? this.pivotFormat['showColumnFilter'] : true;
      this.pivotConfig.showFormat = this.pivotFormat['showFormat'];
      this.pivotConfig.showOptions = this.pivotFormat['showOptions'];
      this.pivotConfig.showPrint = this.pivotFormat['showPrint'];
      this.pivotConfig.showSave = this.pivotFormat['showSave'];
      this.pivotConfig.lineChart = this.pivotFormat['lineChart'];
      this.pivotConfig.barChart = this.pivotFormat['barChart'];
      this.pivotConfig.doughnutChart = this.pivotFormat['doughnutChart'];
      this.pivotConfig.pieChart = this.pivotFormat['pieChart'];
      this.pivotConfig.polarAreaChart = this.pivotFormat['polarAreaChart'];
      this.pivotConfig.radarChart = this.pivotFormat['radarChart'];

      if (this.pivotConfig.reportType === undefined || this.pivotConfig.reportType === null || this.pivotConfig.reportType.trim() === '') {
        this.pivotConfig.reportType = 'grid';
      }
      if (this.pivotConfig.lineChart === undefined || this.pivotConfig.lineChart === null) {
        this.pivotConfig.lineChart = true;
      }
      if (this.pivotConfig.barChart === undefined || this.pivotConfig.barChart === null) {
        this.pivotConfig.barChart = true;
      }
      if (this.pivotConfig.doughnutChart === undefined || this.pivotConfig.doughnutChart === null) {
        this.pivotConfig.doughnutChart = true;
      }
      if (this.pivotConfig.radarChart === undefined || this.pivotConfig.radarChart === null) {
        this.pivotConfig.radarChart = true;
      }
      if (this.pivotConfig.pieChart === undefined || this.pivotConfig.pieChart === null) {
        this.pivotConfig.pieChart = true;
      }
      if (this.pivotConfig.polarAreaChart === undefined || this.pivotConfig.polarAreaChart === null) {
        this.pivotConfig.polarAreaChart = true;
      }
      this.toolbar.setChart(this.pivotConfig.reportType);
      if (this.pivotFormat['columns'] !== undefined) {
        this.selectedColumns = this.helper.copyArray(this.pivotFormat['columns']);
        this.selectedColumns.forEach(f => {
          f.checked = true;
          if (!f.header) {
            f.header = this.helper.getHeaderStyle();
          }
          if (!f.cell) {
            f.cell = this.helper.getCellStyle();
          }
        });
      }
      if (this.pivotFormat['rows'] !== undefined) {
        this.selectedRows = this.helper.copyArray(this.pivotFormat['rows']);
        this.selectedRows.forEach(f => {
          f.checked = true;
          if (!f.header) {
            f.header = this.helper.getHeaderStyle();
          }
          if (!f.cell) {
            f.cell = this.helper.getCellStyle();
          }
        });
      }
      if (this.pivotFormat['values'] !== undefined) {
        this.selectedValues = this.helper.copyArray(this.pivotFormat['values'].map((m: any) => {
          return {
            column: m.column,
            mathFn: m.aggregation
          };
        }));
        this.selectedValues.forEach(value => {
          if (value.column.dataType === 'number') {
            value.mathFns = this.formulas;
          } else if (value.column.dataType === 'date') {
            value.mathFns = this.formulas.filter(f => f.Name === 'Count' || f.Name === 'Distinct Count' || f.Name === 'Min' || f.Name === 'Max');
          } else {
            value.mathFns = this.formulas.filter(f => f.Name === 'Count' || f.Name === 'Distinct Count');
          }
          value.column.checked = true;

          if (!value.column.header) {
            value.column.header = this.helper.getHeaderStyle();
          }
          if (!value.column.cell) {
            value.column.cell = this.helper.getCellStyle();
          }
        });
      }
      if (this.pivotFormat['fields'] !== undefined) {
        this.formulaFields = this.helper.copyArray(this.pivotFormat['fields']);
      }
      if (this.pivotFormat['filters'] !== undefined) {
        this.selectedFColumns = this.helper.copyArray(this.pivotFormat['filters']);
        this.selectedFColumns.forEach(col => {
          if (!col.header) {
            col.header = this.helper.getHeaderStyle();
          }
          if (!col.cell) {
            col.cell = this.helper.getCellStyle();
          }
        });
      }
      if (this.pivotFormat['filtervalues'] !== undefined) {
        this.filters = this.helper.copyArray(this.pivotFormat['filtervalues']);
      }
      if (this.pivotFormat['formattedFields'] !== undefined) {
        this.formattedFields = this.helper.copyArray(this.pivotFormat['formattedFields']);
      }

      const selectedCols: any[] = [];
      selectedCols.push(this.selectedColumns.map(m => m.colName));
      selectedCols.push(this.selectedRows.map(m => m.colName));
      selectedCols.push(this.selectedValues.map(m => m.column.colName));

      this.columns.forEach(f => {
        if (selectedCols.includes(f.colName)) {
          f.checked = true;
        }
      });
      this.filters.forEach(fltr => {
        const obj = this.distValues.filter(f => f.col.colName === fltr.col)[0];
        obj.filter = fltr.filter;
        if (fltr['filterBy']) {
          obj.filterBy = fltr.filterBy;
        }
        if (fltr['startDate']) {
          obj.startDate = new Date(fltr.startDate);
        }
        if (fltr['endDate']) {
          obj.endDate = new Date(fltr.endDate);
        }
      });
      this.pivot.setData(this.data, this.selectedColumns, this.selectedRows, this.selectedValues, this.selectedFColumns, this.formulaFields, this.distValues, this.columns, this.formattedFields);
      this.pivot.drawPivot();
    } else if (this.columns.length > 0 && this.autoConfigurePivot) {
      const strFields = this.columns.filter(f => f.dataType !== 'number');
      if (strFields.length > 0) {
        this.selectedRows = [strFields[0]];
        strFields[0].checked = true;
      }
      const fields = this.columns.filter(f => f.dataType === 'number');
      const obj = new ValueField();
      if (fields.length > 0) {
        obj.column = fields[0];
        obj.mathFn = this.formulas.filter(f => f.Name === 'Sum')[0];
        obj.mathFns = this.formulas;
        fields[0].checked = true;
      } else {
        obj.column = strFields[0];
        obj.mathFn = this.formulas.filter(f => f.Name === 'Count')[0];
        obj.mathFns = this.formulas.filter(f => f.Name === 'Count' || f.Name === 'Distinct Count');
      }
      obj.column.colOrder = 1;
      this.selectedValues.push(obj);
      this.pivot.setData(this.data, this.selectedColumns, this.selectedRows, this.selectedValues, this.selectedFColumns, this.formulaFields, this.distValues, this.columns, this.formattedFields);
      this.pivot.drawPivot();
    }
    this.toolbar.setData(this.columns, this.helper.copyArray(this.selectedColumns), this.helper.copyArray(this.selectedRows), this.helper.copyArray(this.selectedValues), this.helper.copyArray(this.formulaFields), this.helper.copyArray(this.selectedFColumns));
  }

  findColumns() {
    this.columns = [];
    let i = 0;
    Object.keys(this.data[0]).forEach(col => {
      i++;
      const obj = new Column();
      obj.colName = col;
      obj.displayName = col.split(/(?=[A-Z])/).join(' ').replace(/([A-Z])\s(?=[A-Z]\b)/g, '$1');
      obj.selected = false;
      obj.checked = false;
      obj.colOrder = 1;
      obj.dataType = '';
      obj.formula = '';
      obj.fields = [];

      obj.header = this.helper.getHeaderStyle();

      obj.cell = this.helper.getCellStyle();

      obj.valueField = false;
      obj.fontSize = 12;
      obj.fontStyle = 'normal';
      obj.fontWeight = 'normal';
      obj.textOrientation = 'unset';
      this.columns.push(obj);
    });
    this.columns.forEach(col => {
      if (this.distValues.filter(f => f.col.colName == col.colName).length === 0) {
        const obj = new Distinct();
        obj.col = col;
        obj.values = this.distinctPipe.transform(this.data, col);
        obj.filter = [];
        obj.sort = '';
        this.distValues.push(obj);
      }
    });
    this.assignDataType();
    this.columns.filter(f => f.dataType === 'date').forEach(col => {

      const distValue = this.distValues.filter(f => f.col.colName === col.colName)[0];

      const distValueD = this.distinctPipe.transformArr(distValue.values.map(f => {
        return (new Date(f)).getDate();
      }));
      const distValueM = this.distinctPipe.transformArr(distValue.values.map(f => {
        return (new Date(f)).getMonth();
      }));
      const distValueY = this.distinctPipe.transformArr(distValue.values.map(f => {
        return (new Date(f)).getFullYear();
      }));

      const colObjD = this.copyColumn(col);
      colObjD.colName = col.colName + '-Day';
      colObjD.displayName = col.displayName + ' (Day)';
      colObjD.dataType = 'string';
      this.columns.push(colObjD);

      const colObjM = this.copyColumn(col);
      colObjM.colName = col.colName + '-Month';
      colObjM.displayName = col.displayName + ' (Month)';
      colObjM.dataType = 'month';
      this.columns.push(colObjM);

      const colObjY = this.copyColumn(col);
      colObjY.colName = col.colName + '-Year';
      colObjY.displayName = col.displayName + ' (Year)';
      colObjY.dataType = 'string';
      this.columns.push(colObjY);

      const objD = new Distinct();
      objD.col = colObjD;
      objD.values = distValueD;
      objD.filter = [];
      objD.sort = '';

      const objM = new Distinct();
      objM.col = colObjM;
      objM.values = distValueM;
      objM.filter = [];
      objM.sort = '';

      const objY = new Distinct();
      objY.col = colObjY;
      objY.values = distValueY;
      objY.filter = [];
      objY.sort = '';

      this.distValues.push(objD);
      this.distValues.push(objM);
      this.distValues.push(objY);
    });
    this.columns = this.sort.transform(this.columns, 'colName');
  }

  copyColumn(objToCopy: Column): Column {
    const dest = new Column();
    dest.colName = objToCopy.colName;
    dest.displayName = objToCopy.displayName;
    dest.checked = objToCopy.checked;
    dest.selected = objToCopy.selected;
    dest.colOrder = objToCopy.colOrder;
    dest.dataType = objToCopy.dataType;
    dest.header = objToCopy.header;
    if (!dest.header) {
      dest.header = this.helper.getHeaderStyle();
    }
    dest.cell = objToCopy.cell;
    if (!dest.cell) {
      dest.cell = this.helper.getCellStyle();
    }
    dest.thousandSepartor = objToCopy.thousandSepartor;
    dest.decimalPlaces = objToCopy.decimalPlaces;
    dest.currencySymbol = objToCopy.currencySymbol;
    dest.dateFormat = objToCopy.dateFormat;
    dest.valueField = objToCopy.valueField;
    dest.formula = objToCopy.formula;
    dest.fields = objToCopy.fields;
    dest.fontSize = objToCopy.fontSize;
    dest.fontStyle = objToCopy.fontStyle;
    dest.fontWeight = objToCopy.fontWeight;
    dest.textOrientation = objToCopy.textOrientation;
    return dest;
  }

  assignDataType() {
    this.distValues.forEach(val => {
      const vals: any[] = [];
      val.values.forEach(element => {
        if (element !== null && element !== undefined) {
          const dType = this.getDataType(element);
          if (!vals.includes(dType)) {
            vals.push(dType);
          }
          if (dType === 'date') {
            element = this.format.transformStr(element, dType);
          }
        }
      });
      if (vals.length === 1) {
        val.col.dataType = vals[0];
      } else {
        val.col.dataType = 'string';
      }
    });
    this.distValues.forEach(element => {
      this.columns.filter(f => f.colName === element.col.colName).forEach(col => {
        col.dataType = element.col.dataType;
      });
    });
  }

  getDataType(input: any) {
    if (Array.isArray(input)) {
      return 'array';
    } else {
      let dType: string = typeof input;
      if (dType === 'string') {
        if (ISO_8601_FULL.test(input)) {
          const strDate = new Date(input);
          if (strDate instanceof Date && !isNaN(strDate.getTime())) {
            dType = 'date';
          }
        }
      }
      return dType;
    }
  }

  onApplyChanges(e: EventData) {
    this.pivot.showLoading();
    this.selectedColumns = e.Columns;
    this.selectedRows = e.Rows;
    this.selectedValues = e.Values;
    this.selectedFColumns = e.Filters;
    this.formulaFields = e.Fields;
    this.pivot.setData(this.data, this.selectedColumns, this.selectedRows, this.selectedValues, this.selectedFColumns, this.formulaFields, this.distValues, this.columns, this.formattedFields);
    this.pivot.drawPivot();
  }

  onUpdateFormat(e: EventData) {
    this.pivot.showLoading();
    this.selectedColumns = e.Columns;
    this.selectedRows = e.Rows;
    this.selectedValues = e.Values;
    this.pivot.setData(this.data, this.selectedColumns, this.selectedRows, this.selectedValues, this.selectedFColumns, this.formulaFields, this.distValues, this.columns, this.formattedFields);
    this.pivot.drawPivot();
  }

  onExportReport(reporttType: string) {
    this.pivot.exportReport(reporttType);
  }

  onShowFullScreen(isfs: boolean) {
    if (isfs) {
      this.isFullScreen = true;
      if (this.container.nativeElement.requestFullscreen) {
        this.container.nativeElement.requestFullscreen();
      } else if (this.container.nativeElement.mozRequestFullScreen) {
        this.container.nativeElement.mozRequestFullScreen();
      } else if (this.container.nativeElement.webkitRequestFullscreen) {
        this.container.nativeElement.webkitRequestFullscreen();
      }
    } else {
      const document: any = window.document;
      this.isFullScreen = false;
      if (document.exitFullscreen) {
        document.exitFullscreen();
      } else /*if (document.mozCancelFullScreen) {
        document.mozCancelFullScreen();
      } else */ if (document.webkitExitFullscreen) {
          document.webkitExitFullscreen();
        }
    }
  }

  onSaveReport(pivotData: PivotSave) {
    const saveObj: any = {};
    if (pivotData.reportType === 'line') {
      pivotData.lineChart = true;
    }
    if (pivotData.reportType === 'bar') {
      pivotData.barChart = true;
    }
    if (pivotData.reportType === 'doughnut') {
      pivotData.doughnutChart = true;
    }
    if (pivotData.reportType === 'radar') {
      pivotData.radarChart = true;
    }
    if (pivotData.reportType === 'pie') {
      pivotData.pieChart = true;
    }
    if (pivotData.reportType === 'polarArea') {
      pivotData.polarAreaChart = true;
    }
    saveObj['name'] = pivotData.formatName;
    saveObj['reportType'] = pivotData.reportType;
    saveObj['title'] = pivotData.reportTitle;
    saveObj['showOptions'] = pivotData.showOptions;
    saveObj['showFilter'] = pivotData.showFilter;
    saveObj['showAdditionalFilter'] = pivotData.showAdditionalFilter;
    saveObj['showColumnFilter'] = pivotData.showColumnFilter;
    saveObj['showFormat'] = pivotData.showFormat;
    saveObj['showSave'] = pivotData.showSave;
    saveObj['showExport'] = pivotData.showExport;
    saveObj['showPrint'] = pivotData.showPrint;
    saveObj['lineChart'] = pivotData.lineChart;
    saveObj['barChart'] = pivotData.barChart;
    saveObj['doughnutChart'] = pivotData.doughnutChart;
    saveObj['pieChart'] = pivotData.pieChart;
    saveObj['polarAreaChart'] = pivotData.polarAreaChart;
    saveObj['radarChart'] = pivotData.radarChart;

    saveObj['rows'] = this.helper.copyArray(this.selectedRows.map(col => {
      return {
        'colName': col.colName,
        'displayName': col.displayName,
        'colOrder': col.colOrder,
        'dataType': col.dataType,
        'header': {
          'textAlign': col.header ? col.header.textAlign : 'center',
          'fontSize': col.header ? col.header.fontSize : 12,
          'fontStyle': col.header ? col.header.fontStyle : 'normal',
          'fontWeight': col.header ? col.header.fontWeight : 'bold',
          'textOrientation': col.header ? col.header.textOrientation : 'unset'
        },
        'cell': {
          'textAlign': col.cell ? col.cell.textAlign : 'left',
          'fontSize': col.cell ? col.cell.fontSize : 12,
          'fontStyle': col.cell ? col.cell.fontStyle : 'normal',
          'fontWeight': col.cell ? col.cell.fontWeight : 'normal',
          'textOrientation': col.cell ? col.cell.textOrientation : 'unset'
        },
        'thousandSepartor': col.thousandSepartor,
        'decimalPlaces': col.decimalPlaces,
        'currencySymbol': col.currencySymbol,
        'dateFormat': col.dateFormat,
        'valueField': col.valueField,
        'fontSize': col.fontSize,
        'fontStyle': col.fontStyle,
        'fontWeight': col.fontWeight,
        'textOrientation': col.textOrientation,
        'formula': col.formula,
        'fields': col.fields
      };
    }));

    saveObj['columns'] = this.helper.copyArray(this.selectedColumns.map(col => {
      return {
        'colName': col.colName,
        'displayName': col.displayName,
        'colOrder': col.colOrder,
        'dataType': col.dataType,
        'header': {
          'textAlign': col.header ? col.header.textAlign : 'center',
          'fontSize': col.header ? col.header.fontSize : 12,
          'fontStyle': col.header ? col.header.fontStyle : 'normal',
          'fontWeight': col.header ? col.header.fontWeight : 'bold',
          'textOrientation': col.header ? col.header.textOrientation : 'unset'
        },
        'cell': {
          'textAlign': col.cell ? col.cell.textAlign : 'left',
          'fontSize': col.cell ? col.cell.fontSize : 12,
          'fontStyle': col.cell ? col.cell.fontStyle : 'normal',
          'fontWeight': col.cell ? col.cell.fontWeight : 'normal',
          'textOrientation': col.cell ? col.cell.textOrientation : 'unset'
        },
        'thousandSepartor': col.thousandSepartor,
        'decimalPlaces': col.decimalPlaces,
        'currencySymbol': col.currencySymbol,
        'dateFormat': col.dateFormat,
        'valueField': col.valueField,
        'fontSize': col.fontSize,
        'fontStyle': col.fontStyle,
        'fontWeight': col.fontWeight,
        'textOrientation': col.textOrientation,
        'formula': col.formula,
        'fields': col.fields
      };
    }));

    saveObj['values'] = this.helper.copyArray(this.selectedValues.map(col => {
      if (col.column.valueField) {
        return {
          column: {
            'colName': col.column.colName,
            'displayName': col.column.displayName,
            'colOrder': col.column.colOrder,
            'dataType': col.column.dataType,
            'thousandSepartor': col.column.thousandSepartor,
            'decimalPlaces': col.column.decimalPlaces,
            'currencySymbol': col.column.currencySymbol,
            'dateFormat': col.column.dateFormat,
            'valueField': col.column.valueField,
            'fontSize': col.column.fontSize,
            'fontStyle': col.column.fontStyle,
            'fontWeight': col.column.fontWeight,
            'textOrientation': col.column.textOrientation,
            'formula': col.column.formula,
            'fields': col.column.fields
          }
        };
      } else {
        return {
          column: {
            'colName': col.column.colName,
            'displayName': col.column.displayName,
            'colOrder': col.column.colOrder,
            'dataType': col.column.dataType,
            'thousandSepartor': col.column.thousandSepartor,
            'decimalPlaces': col.column.decimalPlaces,
            'currencySymbol': col.column.currencySymbol,
            'dateFormat': col.column.dateFormat,
            'valueField': col.column.valueField,
            'fontSize': col.column.fontSize,
            'fontStyle': col.column.fontStyle,
            'fontWeight': col.column.fontWeight,
            'textOrientation': col.column.textOrientation,
            'formula': col.column.formula,
            'fields': col.column.fields
          },
          aggregation: col.mathFn
        };
      }
    }));

    saveObj['filters'] = this.helper.copyArray(this.selectedFColumns.map(col => {
      return {
        'colName': col.colName,
        'displayName': col.displayName,
        'colOrder': col.colOrder,
        'dataType': col.dataType,
        'header': {
          'textAlign': col.header ? col.header.textAlign : 'center',
          'fontSize': col.header ? col.header.fontSize : 12,
          'fontStyle': col.header ? col.header.fontStyle : 'normal',
          'fontWeight': col.header ? col.header.fontWeight : 'bold',
          'textOrientation': col.header ? col.header.textOrientation : 'unset'
        },
        'cell': {
          'textAlign': col.cell ? col.cell.textAlign : 'left',
          'fontSize': col.cell ? col.cell.fontSize : 12,
          'fontStyle': col.cell ? col.cell.fontStyle : 'normal',
          'fontWeight': col.cell ? col.cell.fontWeight : 'normal',
          'textOrientation': col.cell ? col.cell.textOrientation : 'unset'
        },
        'thousandSepartor': col.thousandSepartor,
        'decimalPlaces': col.decimalPlaces,
        'currencySymbol': col.currencySymbol,
        'dateFormat': col.dateFormat,
        'fontSize': col.fontSize,
        'fontStyle': col.fontStyle,
        'fontWeight': col.fontWeight,
        'textOrientation': col.textOrientation,
        'valueField': col.valueField,
        'formula': col.formula,
        'fields': col.fields
      };
    }));

    if (this.formulaFields.length > 0) {
      saveObj['fields'] = this.helper.copyArray(this.formulaFields);
    }
    saveObj['filtervalues'] = this.helper.copyArray(this.pivot.filters);

    saveObj['formattedFields'] = this.helper.copyObject(this.pivot.formattedFields);

    if (this.isSaveSubscribed) {
      this.savePivot.emit(saveObj);
    } else {
      const a = document.createElement('a');
      const mimeType = 'text/json';
      const blob = new Blob([JSON.stringify(saveObj)], { type: mimeType });
      const url = URL.createObjectURL(blob);
      a.href = url;
      a.setAttribute('download', pivotData.formatName + '.json');
      a.download = pivotData.formatName + '.json';
      document.body.appendChild(a);
      a.click();
      a.remove();
    }
  }

  onLoading(isLoading: boolean) {
    this.isLoading = isLoading;
  }

  onError(err: any) {
    if (this.isErrorSubscribed) {
      this.error.emit(err);
    } else {
      alert(err);
    }
  }
}
